import React from "react";
import { reportReactFatalError } from "../utils/errorReporting";

const styles = {
  root: {
    minHeight: "100vh",
    background: "#2e3a12",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "0",
    position: "relative",
    overflow: "hidden",
    fontFamily: "inherit",
  },
  pawBg: {
    position: "absolute",
    right: -60,
    bottom: -40,
    opacity: 0.07,
    pointerEvents: "none",
    userSelect: "none",
  },
  content: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "64px 72px",
    maxWidth: 580,
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 48,
  },
  brandIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    background: "rgba(255,255,255,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#a8c06a",
    fontSize: 18,
  },
  brandName: {
    fontSize: 13,
    fontWeight: 500,
    color: "#c8d9a8",
    letterSpacing: "0.02em",
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#a8c06a",
    marginBottom: 20,
  },
  eyebrowDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#d4510a",
    flexShrink: 0,
  },
  title: {
    fontSize: 36,
    fontWeight: 500,
    color: "#fdf3e3",
    lineHeight: 1.2,
    margin: "0 0 18px",
  },
  body: {
    fontSize: 16,
    lineHeight: 1.65,
    color: "#b8c89a",
    margin: "0 0 36px",
    maxWidth: 440,
  },
  actions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 40,
  },
  btnPrimary: {
    background: "#d4510a",
    color: "#fdf3e3",
    border: "none",
    borderRadius: 6,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  },
  btnSecondary: {
    background: "rgba(255,255,255,0.07)",
    color: "#c8d9a8",
    border: "0.5px solid rgba(255,255,255,0.15)",
    borderRadius: 6,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  },
  divider: {
    width: 32,
    height: 1,
    background: "rgba(255,255,255,0.12)",
    marginBottom: 20,
  },
  techLabel: {
    fontSize: 11,
    color: "#5a7038",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: 8,
  },
  techBlock: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: 12,
    color: "#7a9450",
    background: "rgba(0,0,0,0.25)",
    borderRadius: 4,
    padding: "10px 14px",
    maxWidth: 440,
    overflowWrap: "anywhere",
    lineHeight: 1.5,
  },
};

const PawIcon = () => (
  <svg
    style={styles.pawBg}
    width="520"
    height="520"
    viewBox="0 0 200 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <ellipse cx="100" cy="130" rx="52" ry="42" fill="white" />
    <ellipse cx="60" cy="85" rx="18" ry="22" fill="white" />
    <ellipse cx="100" cy="76" rx="16" ry="20" fill="white" />
    <ellipse cx="140" cy="85" rx="18" ry="22" fill="white" />
    <ellipse cx="38" cy="112" rx="12" ry="16" fill="white" />
    <ellipse cx="162" cy="112" rx="12" ry="16" fill="white" />
  </svg>
);

class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, hasError: false, payload: null };
  }

  static getDerivedStateFromError(error) {
    return { error, hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const payload = reportReactFatalError(error, errorInfo);
    this.setState({ payload });
  }

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  handleGoHome = () => {
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    const { children } = this.props;
    const { error, hasError, payload } = this.state;

    if (!hasError) return children;

    const technicalMessage =
      payload?.message || error?.message || "No hay mensaje técnico disponible.";

    return (
      <main style={styles.root}>
        <PawIcon />

        <div style={styles.content}>
          <div style={styles.brand}>
            <div style={styles.brandIcon}>
              {/* Sustituir por tu logo si lo tienes como componente */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 9a2 2 0 1 0 4 0 2 2 0 0 0 -4 0" />
                <path d="M12.5 4a2 2 0 1 0 4 0 2 2 0 0 0 -4 0" />
                <path d="M16.5 9a2 2 0 1 0 4 0 2 2 0 0 0 -4 0" />
                <path d="M4 9a2 2 0 1 0 4 0 2 2 0 0 0 -4 0" />
                <path d="M5 15c-.667 2 1 4 3.5 4.5s5 0 6.5 -1.5 2 -3.5 1 -5 -2.833 -2.833 -5 -3a5 5 0 0 0 -6 5z" />
              </svg>
            </div>
            <span style={styles.brandName}>Pawsi Patitas</span>
          </div>

          <div style={styles.eyebrow}>
            <span style={styles.eyebrowDot} />
            Error en pantalla
          </div>

          <h1 style={styles.title}>
            Algo salió mal<br />en esta pantalla
          </h1>

          <p style={styles.body}>
            No asumas que la operación quedó guardada — verifica el estado en
            el sistema antes de continuar. Puedes recargar esta pantalla o
            volver al inicio.
          </p>

          <div style={styles.actions}>
            <button onClick={this.handleReload} style={styles.btnPrimary} type="button">
              Recargar pantalla
            </button>
            <button onClick={this.handleGoHome} style={styles.btnSecondary} type="button">
              Ir al inicio
            </button>
          </div>

          <div style={styles.divider} />

          <p style={styles.techLabel}>Detalle técnico</p>
          <div style={styles.techBlock}>{technicalMessage}</div>
        </div>
      </main>
    );
  }
}

export default GlobalErrorBoundary;